import React, { useMemo, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import {
  CheckCircleFill,
  ChevronLeft,
  ChevronRight,
  DashCircleFill,
  Search,
  XCircleFill,
} from "react-bootstrap-icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { addDays, format, subDays } from "date-fns";
import NavHeader from "../Common/NavHeader";
import Paginator from "../Common/Paginator";
import { useGetSimplifiedReportStatus } from "./api";
import "./analytics.css";

const ROWS_PER_PAGE = 40;

const TAG = {
  TRUE: ["green", CheckCircleFill],
  FALSE: ["red", XCircleFill],
  "No Sales": ["yellow", DashCircleFill],
};

const StatusTag = ({ value }) => {
  const [color, Icon] = TAG[value] || ["grey", DashCircleFill];
  return (
    <span className={`cb-tag cb-tag-${color}`}>
      <Icon size={12} />
      {value || "NA"}
    </span>
  );
};

const COLUMNS = [
  { key: "restaurant_id", label: "Restaurant ID", width: "18%" },
  { key: "restaurant_name", label: "Restaurant Name", width: "24%", bold: true },
  { key: "sync_status", label: "Sync Status", width: "13%", tag: true, sortable: true },
  { key: "sync_mode", label: "Sync Mode", width: "13%", tag: true, sortable: true },
  { key: "reason", label: "Reason", width: "22%" },
  { key: "report_date", label: "Date", width: "10%" },
];

const FILTERS = [
  { key: "sync_status", label: "Sync Status" },
  { key: "sync_mode", label: "Sync Mode" },
];

export default function Analytics() {
  const [startDate, setStartDate] = useState(subDays(new Date(), 1));
  const [searchTerm, setSearchTerm] = useState("");
  const [sort, setSort] = useState({ key: null, asc: true });
  const [pageNum, setPageNum] = useState(1);
  // "" = all; null values match "NA"
  const [filters, setFilters] = useState({ sync_status: "", sync_mode: "" });

  const reportDate = format(startDate, "yyyy-MM-dd");
  const { data = [], isLoading } = useGetSimplifiedReportStatus(reportDate);

  const options = useMemo(
    () =>
      Object.fromEntries(
        FILTERS.map(({ key }) => [key, [...new Set(data.map((r) => r[key] || "NA"))].sort()])
      ),
    [data]
  );

  const rows = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const filtered = data.filter(
      (r) =>
        (r.restaurant_name || "").toLowerCase().includes(term) &&
        FILTERS.every(({ key }) => !filters[key] || (r[key] || "NA") === filters[key])
    );
    if (sort.key) {
      const dir = sort.asc ? 1 : -1;
      // nulls always last
      filtered.sort((a, b) => {
        if (a[sort.key] == null) return b[sort.key] == null ? 0 : 1;
        if (b[sort.key] == null) return -1;
        return String(a[sort.key]).localeCompare(String(b[sort.key])) * dir;
      });
    }
    return filtered;
  }, [data, searchTerm, sort, filters]);

  // chips summarise the whole day, not the filtered view
  const summary = [
    { label: "Total", value: data.length },
    { label: "Synced", value: data.filter((r) => r.sync_status === "TRUE").length },
    { label: "Not Synced", value: data.filter((r) => r.sync_status === "FALSE").length },
    { label: "No Sales", value: data.filter((r) => r.sync_status === "No Sales").length },
  ];

  const totalPages = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
  const page = Math.min(pageNum, totalPages);
  const pageRows = rows.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  const changeDate = (d) => {
    if (!d) return;
    setStartDate(d);
    setPageNum(1);
  };
  const toggleSort = (key) =>
    setSort((s) => ({ key, asc: s.key === key ? !s.asc : true }));

  return (
    <>
      <NavHeader view="" />
      <div className="cb-page">
        <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-3">
          <div>
            <h1 className="cb-title">Crunch Analytics</h1>
            <div className="cb-subtitle">Sync result per restaurant for {reportDate}</div>
          </div>
          {!isLoading && (
            <div className="d-flex flex-wrap gap-2">
              {summary.map((s) => (
                <div key={s.label} className="cb-chip">
                  <span className="cb-chip-label">{s.label}</span>
                  <span className="cb-chip-value">{s.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="cb-card">
          <div className="d-flex flex-wrap align-items-center gap-2 p-3">
            <div className="cb-search flex-grow-1" style={{ maxWidth: "320px" }}>
              <Search size={14} />
              <input
                className="form-control cb-control"
                placeholder="Search restaurants"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPageNum(1);
                }}
              />
            </div>
            {FILTERS.map(({ key, label }) => (
              <Form.Select
                key={key}
                aria-label={label}
                className="cb-control"
                style={{ width: "170px" }}
                value={filters[key]}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, [key]: e.target.value }));
                  setPageNum(1);
                }}
              >
                <option value="">All {label}</option>
                {options[key].map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </Form.Select>
            ))}
            <div className="cb-date d-flex align-items-center gap-1 ms-md-auto">
              <button type="button" className="cb-icon-btn" aria-label="Previous day" onClick={() => changeDate(subDays(startDate, 1))}>
                <ChevronLeft size={14} />
              </button>
              <DatePicker
                selected={startDate}
                onChange={changeDate}
                dateFormat="MMM d, yyyy"
                className="form-control cb-control"
              />
              <button type="button" className="cb-icon-btn" aria-label="Next day" onClick={() => changeDate(addDays(startDate, 1))}>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" style={{ color: "var(--cb-primary)" }} />
            </div>
          ) : !rows.length ? (
            <div className="text-center py-5">
              <div className="fw-semibold">No report status found</div>
              <div className="cb-subtitle">Try another date or clear the filters.</div>
            </div>
          ) : (
            <>
              <div className="cb-scroll">
                <table className="cb-table table-stack">
                  <thead>
                    <tr>
                      {COLUMNS.map((c) => (
                        <th key={c.key} style={{ width: c.width }}>
                          {c.sortable ? (
                            <button
                              type="button"
                              className={`cb-sort ${sort.key === c.key ? "active" : ""}`}
                              onClick={() => toggleSort(c.key)}
                            >
                              {c.label}
                              <span aria-hidden>{sort.key === c.key ? (sort.asc ? "▲" : "▼") : "↕"}</span>
                            </button>
                          ) : (
                            c.label
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((r) => (
                      <tr key={r._id}>
                        {COLUMNS.map((c) => (
                          <td key={c.key} data-label={c.label} className={c.bold ? "fw-medium" : ""}>
                            {c.tag ? <StatusTag value={r[c.key]} /> : r[c.key] || "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 p-3">
                <span className="cb-subtitle">
                  Showing {(page - 1) * ROWS_PER_PAGE + 1}–{Math.min(page * ROWS_PER_PAGE, rows.length)} of {rows.length}
                </span>
                {totalPages > 1 && (
                  <Paginator pageNum={page} setPageNum={setPageNum} totalPages={totalPages} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
